import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Check } from 'components/Icons';
import { HeaderContext } from '../../headerContext';

const StyledListEntry = styled.li`
  button {
    ${(props) => props.theme.typography.body};
    align-items: center;
    background: none;
    border: none;
    display: flex;
    cursor: pointer;
    overflow: hidden;
    padding: 0.5rem 0;
    text-align: left;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;

    &.active {
      background-color: ${(props) => props.theme.colors.yellow.primary};
    }

    span:first-child {
      text-align: center;
      width: 3rem;
    }
  }

  &[aria-selected='true'] {
    button {
      ${(props) => props.theme.typography.bodyBold};
      background-color: ${(props) => props.theme.colors.yellow.primary};
    }
  }
`;

const StyledMenu = styled.ul`
  list-style: none;
  padding-left: 0;

  h4 {
    ${(props) => props.theme.typography.subHeader};
    color: ${(props) => props.theme.colors.grayscale.gray0};
    margin: 0;
    padding: 0 0 0.5rem;
  }
`;

const StyledCheck = styled(Check)`
  height: 1.25rem;
`;

function MobileRegionSelector({ selectedItem, onClick }) {
  const headerContext = useContext(HeaderContext);
  const { regions } = headerContext;

  const handleRegionClick = (region) => {
    if (typeof onClick === 'function') {
      onClick(region);
      return;
    }

    console.error(
      'onClick passed to MobileRegionSelector is not a valid function'
    );
  };

  /**
   * Renders the region options in Downshift. Filters out
   * invalid region objects and pushes the elements to render
   * in the returned array.
   *
   * @param {Object} selectedItem currently selected item
   * @returns {Array} items to render
   */
  const renderRegions = () => {
    const itemsToRender = [];

    Object.keys(regions).forEach((country) => {
      // renders a country title before each regions array
      itemsToRender.push(<h4 key={country}>{country}</h4>);

      const sortedRegions = regions[country]
        .slice()
        .sort((a, b) => a.sortOrder - b.sortOrder);

      sortedRegions.forEach((region) => {
        if (Number.isInteger(region.id) && typeof region.title === 'string') {
          itemsToRender.push(
            <StyledListEntry key={region.id} item={region}>
              <button
                type="button"
                id={`mobile-regions-list-${region.title.replace(/ /, '-')}`}
                className={selectedItem.id === region.id ? 'active' : ''}
                onClick={() => handleRegionClick(region)}
              >
                <span>
                  {selectedItem.id === region.id ? <StyledCheck /> : null}
                </span>
                <span>{region.title}</span>
              </button>
            </StyledListEntry>
          );
        } else {
          console.error(
            'Invalid region data in MobileRegionSelector component:',
            region
          );
        }
      });
    });

    return itemsToRender;
  };

  return regions ? <StyledMenu>{renderRegions()}</StyledMenu> : null;
}

MobileRegionSelector.displayName = 'MobileRegionSelector';

/**
 * propTypes for this component
 */
MobileRegionSelector.propTypes = {
  /** the currently selected region */
  selectedItem: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
  }).isRequired,
  /** onClick handler for selecting a region */
  onClick: PropTypes.func.isRequired,
};

/**
 * Default prop types
 */
MobileRegionSelector.defaultProps = {};

export default MobileRegionSelector;
